import { Suspense, useEffect, type ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { lazyRoute } from "./app-recovery";
import * as api from "./api/disciplio-api";
import { AppShell } from "./components/AppShell";
import { useDisciplio } from "./context/disciplio-context";
import { useI18n } from "./context/i18n-context";

const TodayPage = lazyRoute(() => import("./pages/TodayPage").then((module) => ({ default: module.TodayPage })));
const WeekPage = lazyRoute(() => import("./pages/WeekPage").then((module) => ({ default: module.WeekPage })));
const PlanningPage = lazyRoute(() => import("./pages/PlanningPage").then((module) => ({ default: module.PlanningPage })));
const CoachPage = lazyRoute(() => import("./pages/CoachPage").then((module) => ({ default: module.CoachPage })));
const InsightsPage = lazyRoute(() => import("./pages/InsightsPage").then((module) => ({ default: module.InsightsPage })));
const CalendarPage = lazyRoute(() => import("./pages/CalendarPage").then((module) => ({ default: module.CalendarPage })));
const WelcomePage = lazyRoute(() => import("./pages/WelcomePage").then((module) => ({ default: module.WelcomePage })));
const RegisterPage = lazyRoute(() => import("./pages/RegisterPage").then((module) => ({ default: module.RegisterPage })));
const LoginPage = lazyRoute(() => import("./pages/LoginPage").then((module) => ({ default: module.LoginPage })));
const AccountPage = lazyRoute(() => import("./pages/AccountPage").then((module) => ({ default: module.AccountPage })));
const RagAdminPage = lazyRoute(() => import("./pages/RagAdminPage").then((module) => ({ default: module.RagAdminPage })));
const AiUsageAdminPage = lazyRoute(() => import("./pages/AiUsageAdminPage").then((module) => ({ default: module.AiUsageAdminPage })));
const PolicyAcceptancePage = lazyRoute(() => import("./pages/PolicyAcceptancePage").then((module) => ({ default: module.PolicyAcceptancePage })));
const ResetPasswordPage = lazyRoute(() => import("./pages/ResetPasswordPage").then((module) => ({ default: module.ResetPasswordPage })));
const VerifyEmailPage = lazyRoute(() => import("./pages/VerifyEmailPage").then((module) => ({ default: module.VerifyEmailPage })));
const ForgotPasswordPage = lazyRoute(() => import("./pages/ForgotPasswordPage").then((module) => ({ default: module.ForgotPasswordPage })));

function RouteFallback() {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: "24px",
        color: "rgba(240, 240, 245, 0.78)",
        background: "#09090b",
      }}
    >
      Loading
    </div>
  );
}

function withRouteFallback(children: ReactNode) {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
}

function RemoteLoadError({ onRetry, retryAfterSeconds }: { onRetry: () => Promise<void>; retryAfterSeconds?: number | null }) {
  const { t } = useI18n();
  return (
    <div className="public-page">
      <main className="legal-document policy-acceptance" id="main" role="alert">
        <p className="label">{t("sessionLoad.label")}</p>
        <h1>{t("sessionLoad.title")}</h1>
        <p className="legal-document__intro">
          {retryAfterSeconds != null
            ? t("sessionLoad.rateLimited", { seconds: retryAfterSeconds })
            : t("sessionLoad.description")}
        </p>
        <button className="btn btn--primary" onClick={() => void onRetry()}>
          {t("sessionLoad.retry")}
        </button>
      </main>
    </div>
  );
}

/** Unauthenticated protected routes redirect to /login, not /welcome. */
export function RequireAuth({ children }: { children: ReactNode }) {
  if (!api.hasApiSession()) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

export function RequireGuest({ children }: { children: ReactNode }) {
  if (api.hasApiSession()) {
    return <Navigate to="/" replace />;
  }
  return children;
}

/** A guest visiting /welcome goes to /register -- account creation, not onboarding, is the entry point. */
export function RequireAuthOrRegister({ children }: { children: ReactNode }) {
  if (!api.hasApiSession()) {
    return <Navigate to="/register" replace />;
  }
  return children;
}

/** Waits for remote profile hydration so onboarding status is never decided from stale/default state. */
export function RequireIncompleteOnboarding({ children }: { children: ReactNode }) {
  const { state, isRemoteHydrated } = useDisciplio();
  if (!isRemoteHydrated) return <RouteFallback />;
  if (state.profile.onboardingCompleted) {
    return <Navigate to="/" replace />;
  }
  return children;
}

export function RequireCompletedOnboarding({ children }: { children: ReactNode }) {
  const { state, isRemoteHydrated } = useDisciplio();
  if (!isRemoteHydrated) return <RouteFallback />;
  if (!state.profile.onboardingCompleted) {
    return <Navigate to="/welcome" replace />;
  }
  return children;
}

function RequireAdmin({ children }: { children: ReactNode }) {
  const { state } = useDisciplio();
  if (!api.hasApiSession()) {
    return <Navigate to="/login" replace />;
  }
  if (state.profile.accountRole !== "ADMIN") {
    return <Navigate to="/" replace />;
  }
  return children;
}

export function RequireCurrentPolicies({ children }: { children: ReactNode }) {
  const { state, isRemoteHydrated } = useDisciplio();
  if (!isRemoteHydrated) return <RouteFallback />;
  if (!state.profile.legal?.termsAccepted || !state.profile.legal?.privacyAccepted) {
    return <Navigate to="/legal/acceptance" replace />;
  }
  return children;
}

export function RequirePendingPolicies({ children }: { children: ReactNode }) {
  const { state, isRemoteHydrated } = useDisciplio();
  if (!isRemoteHydrated) return <RouteFallback />;
  if (state.profile.legal?.termsAccepted && state.profile.legal?.privacyAccepted) {
    return <Navigate to="/" replace />;
  }
  return children;
}

export function App() {
  const {
    state,
    isRemoteHydrated,
    isSessionReady,
    remoteLoadError,
    retryRemoteState,
    sessionRestoreError,
    retrySessionRestore,
  } = useDisciplio();
  const { setLanguage } = useI18n();

  useEffect(() => {
    if (!api.hasApiSession() || !isRemoteHydrated) return;
    const serverLanguage = api.normalizeLanguageForApi(state.settings.language);
    setLanguage(serverLanguage as "en" | "pl" | "uk");
  }, [isRemoteHydrated, setLanguage, state.settings.language]);

  if (!isSessionReady) return <RouteFallback />;
  if (sessionRestoreError) {
    return (
      <RemoteLoadError
        onRetry={retrySessionRestore}
        retryAfterSeconds={sessionRestoreError.retryAfterSeconds}
      />
    );
  }
  if (remoteLoadError) return <RemoteLoadError onRetry={retryRemoteState} />;

  return (
    <Routes>
      <Route
        path="/welcome"
        element={<RequireAuthOrRegister><RequireIncompleteOnboarding>{withRouteFallback(<WelcomePage />)}</RequireIncompleteOnboarding></RequireAuthOrRegister>}
      />
      <Route path="/register" element={<RequireGuest>{withRouteFallback(<RegisterPage />)}</RequireGuest>} />
      <Route path="/login" element={<RequireGuest>{withRouteFallback(<LoginPage />)}</RequireGuest>} />
      <Route path="/reset-password" element={withRouteFallback(<ResetPasswordPage />)} />
      <Route path="/verify-email" element={withRouteFallback(<VerifyEmailPage />)} />
      <Route path="/forgot-password" element={<RequireGuest>{withRouteFallback(<ForgotPasswordPage />)}</RequireGuest>} />
      <Route path="/legal/acceptance" element={<RequireAuth><RequirePendingPolicies>{withRouteFallback(<PolicyAcceptancePage />)}</RequirePendingPolicies></RequireAuth>} />
      <Route element={<RequireAuth><RequireCompletedOnboarding><RequireCurrentPolicies><AppShell /></RequireCurrentPolicies></RequireCompletedOnboarding></RequireAuth>}>
        <Route index element={withRouteFallback(<TodayPage />)} />
        <Route path="goals" element={withRouteFallback(<WeekPage />)} />
        <Route path="week" element={<Navigate to="/goals" replace />} />
        <Route path="calendar" element={withRouteFallback(<CalendarPage />)} />
        <Route path="planning" element={withRouteFallback(<PlanningPage />)} />
        <Route path="insights" element={withRouteFallback(<InsightsPage />)} />
        <Route path="coach" element={withRouteFallback(<CoachPage />)} />
        <Route path="account" element={withRouteFallback(<AccountPage />)} />
        <Route path="account/profile" element={withRouteFallback(<AccountPage />)} />
        <Route path="account/settings" element={withRouteFallback(<AccountPage />)} />
        <Route path="account/ai" element={withRouteFallback(<AccountPage />)} />
        <Route path="account/privacy" element={withRouteFallback(<AccountPage />)} />
        <Route path="admin/rag" element={<RequireAdmin>{withRouteFallback(<RagAdminPage />)}</RequireAdmin>} />
        <Route path="admin/ai-usage" element={<RequireAdmin>{withRouteFallback(<AiUsageAdminPage />)}</RequireAdmin>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
